import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardHeader,
  Badge,
  getStatusBadgeVariant,
  ContentLoader,
  Button,
  Input,
  Select,
  Pagination,
  StatsCard,
  StatsGrid,
  EntityLink,
  BackButton,
} from '@/components/common';
import {
  useUnifiedChannel,
  useChannelPayoutOverrides,
  useCreateChannelPayoutOverride,
  useDeleteChannelPayoutOverride,
} from '@/hooks/useChannels';
import { usePOSPPolicies, usePOSPEarningsSummary } from '@/hooks/useBrokers';
import { useBrokerInsuranceCompanies } from '@/hooks/useBrokerInsuranceCompanies';
import { useInsurerInsuranceTypes } from '@/hooks/useCommissions';
import { formatDate, formatCurrency, titleCase } from '@/utils';
import { resolveChannelDetailView } from './channelDetailView';
import { PartnerLoginPanel } from './PartnerLoginPanel';
import { useAuthStore } from '@/store/auth';
import { useCan } from '@/hooks/useEmployeeRights';
import { PeriodToggle, type ProfilePeriod } from './PeriodToggle';
import { PospLedgerPanel } from './PospLedgerPanel';
import { PospBankDetailsPanel } from './PospBankDetailsPanel';
import { PospTrainingPanel } from './PospTrainingPanel';
import { PospLoginSection } from './PospOnboardingPanel';
import { ChannelKycDocumentsPanel } from './ChannelKycDocumentsPanel';
import { channelPayoutBasisOptions, basisHidesOdTp, BROKER_COMMISSION_HINT } from './payoutBasis';
import { PolicyNumber } from '@/components/common/PolicyNumber';

export function ChannelDetailPage() {
  const { id } = useParams<{ id: string }>();
  // B4: verifying or rejecting a channel's KYC document is channels Edit.
  const canVerifyChannelDocuments = useCan('channels', 'edit');
  const isBrokerAdmin = useAuthStore((s) => s.user?.role) === 'broker_admin';
  // P1: stepping an agent's compliance is a different gate. The server floors
  // `advance_compliance` on `_manage_posps_gate()`, i.e. the legacy
  // `manage_posps` right, which resolves to the channels CREATE cell
  // (`LEGACY_CODE_LEVEL['manage_posps']`) — the same cell POSPsListPage reads.
  // Edit and create are independent ticks in the matrix, so an employee granted
  // KYC verification alone must not be shown compliance controls that always
  // 403, and one granted manage_posps alone must not lose them.
  const canManagePosps = useCan('channels', 'create');

  // Ask the server for THIS row. `unified` carries all the summary data the
  // page needs, and its `?id=` filter runs against the merged list before
  // pagination, so the answer does not depend on how many channels the broker
  // has.
  //
  // This used to load page 1 of the list and scan the 20 rows it got back. The
  // broker who reported it has 30 agents, so agents 21-30 were unreachable:
  // the redirect landed on the right URL, every request returned 200, and the
  // page said "Channel not found". A bigger page size would only have moved
  // the cliff.
  const { data, isLoading } = useUnifiedChannel(id);
  const channel = data?.results[0];

  if (isLoading) return <ContentLoader />;

  if (!channel) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Channel not found</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">The channel you are looking for does not exist or is not accessible.</p>
        <Link to="/broker/channels" className="mt-4 inline-block">
          <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
            Back to Channels
          </Button>
        </Link>
      </div>
    );
  }

  const view = resolveChannelDetailView(channel);
  // See the panel below for why this is `channel_id` first.
  const channelDocumentId =
    channel.entity_kind === 'channel' ? channel.id : channel.channel_id ?? null;

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/broker/channels" />
      </div>
      <PageHeader
        title={channel.name}
        subtitle={channel.code || undefined}
        breadcrumbs={[
          { label: 'Channels', href: '/broker/channels' },
          { label: channel.name },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Summary */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader title="Channel Summary" />
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <dt className="text-xs text-gray-500 dark:text-gray-400">Type</dt>
                <dd className="mt-1">
                  {/* Board #252: show the custom type's own name, and say what
                      it behaves as -- the list badge does the same, so the two
                      views cannot disagree about what this channel is. */}
                  <Badge variant="info" size="sm">{channel.type_display ?? channel.type_label}</Badge>
                  {channel.type_display && channel.type_display !== channel.type_label && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Behaves as {channel.type_label}</p>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 dark:text-gray-400">Status</dt>
                <dd className="mt-1">
                  <Badge variant={getStatusBadgeVariant(channel.status)} size="sm">
                    {titleCase(channel.status)}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 dark:text-gray-400">Code</dt>
                <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-gray-100">{channel.code || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 dark:text-gray-400">Policies</dt>
                <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{channel.policy_count}</dd>
              </div>
              {channel.email && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{channel.email}</dd>
                </div>
              )}
              {channel.phone && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400">Phone</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{channel.phone}</dd>
                </div>
              )}
            </dl>
          </Card>

          {/* Commission summary — non-POSP channels don't expose policy-level commission.
              ALL-TIME by design (item 14 decision): this feeds every broker_admin list
              view via /brokers/channels/unified/, so it deliberately stays unscoped —
              the label just needs to say so, so it's never mistaken for the FY figure
              in the Policies & Earnings panel below. */}
          <Card>
            <CardHeader title="Commission (Lifetime)" />
            {channel.commission_earned === 'N-A' ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Commission data is not available for this channel type. Individual policy commission
                is tracked at the POSP level.
              </p>
            ) : (
              <>
                <p className="text-sm text-gray-900 dark:text-gray-100 font-medium">{channel.commission_earned}</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  All-time total — see Policies &amp; Earnings below for this financial year.
                </p>
              </>
            )}
          </Card>

          {/* KYC documents for this channel. The SAME rows the channel's own
              My Portal renders, which is the point of the requirement: an
              upload on either screen appears on the other, and the status and
              remarks recorded here are what the channel reads back.
              `channel_id` rather than `id`: a POSP-backed channel surfaces as
              a `posp` unified row whose `id` is the AGENT, with the channel on
              `channel_id`. Posting documents against the agent id would 400 at
              best and attach them to the wrong row at worst. */}
          {channelDocumentId && (
            <ChannelKycDocumentsPanel channelId={channelDocumentId} canVerify={canVerifyChannelDocuments} />
          )}

          {/* Type-aware detail: POSP channels show a real policy list and FY
              earnings; others show payout override. The POSP Onboarding
              stepper used to render here too, but that activation journey
              isn't a state machine the system actually drives — it stays on
              the POSP's own self-service profile (POSPDetailPage) only. */}
          {view.mode === 'posp' && view.pospId ? (
            <>
              {/* POSP-1(d): READ-ONLY, and masked by the server before it ever
                  reaches this page — `broker-channels/unified/` sends
                  `bank_account_number_masked` and never the number. No Edit
                  button, and the agent's own bank-details endpoint would 403 a
                  broker admin even if one were drawn: the mask is the boundary,
                  not the missing button. */}
              {/* M-05: the agent's login, from the broker admin's side. The
                  server gates create-login on `_manage_posps_gate` (the
                  channels CREATE cell) and 404s any other broker's agent; a
                  reset also signs the agent out everywhere. */}
              {canManagePosps && (
                <PospLoginSection pospId={view.pospId} passwordSet={channel.password_set} />
              )}
              <PospBankDetailsPanel
                pospId={view.pospId}
                bankName={channel.bank_name}
                ifscCode={channel.bank_ifsc_code}
                maskedAccountNumber={channel.bank_account_number_masked}
                canEdit={false}
              />
              {/* P1. The agent's IRDAI compliance record: the facts, the stage
                  and the controls that step it. It sits above the numbers
                  because it is what a Broker Admin opens this page to MAINTAIN
                  — the policies and ledger below are what they open it to
                  read. Nothing on it gates anything (decision 15). */}
              <PospTrainingPanel pospId={view.pospId} canManage={canManagePosps} />
              <PospPoliciesPanel pospId={view.pospId} />
              {/* B1: the reported gap. The policies panel above answers "what
                  did they sell"; this answers "what are they owed and what has
                  been paid", which is the question an agent's page is usually
                  opened for -- and the data has been there all along, one
                  screen away behind a filter. */}
              <PospLedgerPanel pospId={view.pospId} pospName={channel.name} />
            </>
          ) : (
            <Card>
              <CardHeader
                title="Payout Override"
                subtitle="Customise this channel's payout, overriding its channel type's grid"
              />
              <ChannelOverrides channelId={channel.id} />
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Partner portal, phase 1: broker admin only. The panel asks the server whether this channel is
              eligible or already has partner access, so a renamed channel type still shows it. */}
          {channel.entity_kind === 'channel' && isBrokerAdmin && (
            <PartnerLoginPanel channelId={channel.id} />
          )}
          <Card>
            <CardHeader title="Quick Actions" />
            <div className="space-y-3">
              {channel.entity_kind === 'channel' && canVerifyChannelDocuments && (
                <Link to={`/broker/channels/${channel.id}/workspace`} className="block">
                  <Button variant="outline" className="w-full justify-start">
                    Preview as partner (read-only)
                  </Button>
                </Link>
              )}
              <Link to="/broker/channels" className="block">
                <Button variant="outline" className="w-full justify-start" leftIcon={<ArrowLeftIcon className="w-4 h-4" />}>
                  Back to Channels
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

/** Item 14: the POSP holistic profile for the broker_admin/employee path.
 *  Before this, ChannelDetailPage's POSP branch showed only a policy COUNT
 *  (channel.policy_count) and the all-time commission card above — no policy
 *  LIST and no financial-year framing, unlike the self-service POSPDetailPage.
 *  This mirrors POSPDetailPage's policies table (POSPDetailPage.tsx:102-187)
 *  and adds the real FY-earnings aggregate (GET .../earnings_summary/) — a
 *  single backend aggregate query, not a client-side page sum. Defaults to the
 *  current financial year with an All-time toggle (decision in the item-14
 *  diagnosis: the toggle is cheap since POSPPolicyFilters already supports
 *  date_from/date_to). */
function PospPoliciesPanel({ pospId }: { pospId: string }) {
  const [page, setPage] = useState(1);
  const [period, setPeriod] = useState<ProfilePeriod>('fy');

  const { data: policiesData, isLoading: policiesLoading } = usePOSPPolicies(pospId, page, { period });
  const { data: earningsSummary, isLoading: earningsLoading } = usePOSPEarningsSummary(pospId, { period });

  const handlePeriodChange = (next: ProfilePeriod) => {
    setPeriod(next);
    setPage(1);
  };

  // Broker-side view: commission_summary_for hands back
  // received/paid_to_posp/broker/margin. "Earnings" here means what this POSP
  // was actually PAID, not the broker's own margin — paid_to_posp.
  const earningsValue = earningsSummary?.paid_to_posp ?? earningsSummary?.your_commission ?? null;

  return (
    <Card padding="none">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Policies &amp; Earnings</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {period === 'fy' ? 'Current financial year (Apr–Mar)' : 'All-time'}
          </p>
        </div>
        <PeriodToggle period={period} onChange={handlePeriodChange} />
      </div>

      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
        <StatsGrid columns={2}>
          <StatsCard
            title="Policies"
            value={policiesData?.count ?? 0}
            loading={policiesLoading}
            subtitle="Policies issued via this POSP"
          />
          {/* "Paid" was wrong: `paid_to_posp` is Sum(posp_commission) over the
              calculations -- what the agent EARNED, not what was disbursed
              (apps/commissions/visibility.py:299). Harmless while it stood
              alone; actively misleading now that the ledger's "Outstanding"
              sits directly below it, because ₹100k "paid" above ₹40k
              outstanding reads as ₹140k on an agent who has had ₹60k. */}
          <StatsCard
            title="Commission Earned"
            value={earningsValue != null ? formatCurrency(earningsValue) : '—'}
            loading={earningsLoading}
            subtitle={period === 'fy' ? 'This financial year' : 'All-time'}
          />
        </StatsGrid>
      </div>

      {policiesLoading ? (
        <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading policies…</div>
      ) : policiesData && policiesData.results.length > 0 ? (() => {
        const hasCommission = policiesData.results.some((p) => p.commission);
        return (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Policy #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Holder</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Premium</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Date</th>
                    {hasCommission && (
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Commission</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {policiesData.results.map((policy) => {
                    const commValue =
                      policy.commission?.paid_to_posp ??
                      policy.commission?.your_commission ??
                      null;
                    return (
                      <tr key={policy.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-4 py-3 text-sm">
                          <PolicyNumber source={policy} render={(text) => <EntityLink kind="policy" id={policy.id} name={text} />} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {policy.customer ? (
                            <EntityLink kind="contact" id={policy.customer} name={policy.holder_name} />
                          ) : (
                            policy.holder_name
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {formatCurrency(policy.total_premium)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={getStatusBadgeVariant(policy.status)} size="sm">
                            {titleCase(policy.status)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {policy.policy_date
                            ? formatDate(policy.policy_date)
                            : policy.inception_date
                              ? formatDate(policy.inception_date)
                              : '—'}
                        </td>
                        {hasCommission && (
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">
                            {commValue != null ? formatCurrency(commValue) : <span className="text-gray-400">—</span>}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {Math.ceil((policiesData.count ?? 0) / 20) > 1 && (
              <Pagination
                currentPage={page}
                totalPages={Math.ceil((policiesData.count ?? 0) / 20)}
                totalItems={policiesData.count ?? 0}
                itemsPerPage={20}
                onPageChange={setPage}
              />
            )}
          </>
        );
      })() : (
        <div className="p-6 text-sm text-gray-500 dark:text-gray-400">No policies found for this POSP.</div>
      )}
    </Card>
  );
}

function ChannelOverrides({ channelId }: { channelId: string }) {
  const { data, isLoading } = useChannelPayoutOverrides(channelId);
  const overrides = data?.results ?? [];
  const createOverride = useCreateChannelPayoutOverride();
  const deleteOverride = useDeleteChannelPayoutOverride();

  const { data: insurers } = useBrokerInsuranceCompanies();
  const [insurer, setInsurer] = useState('');
  const [insuranceType, setInsuranceType] = useState('');
  const [percentage, setPercentage] = useState('');
  const [premiumBase, setPremiumBase] = useState('net_premium');
  const { data: insurerTypes } = useInsurerInsuranceTypes(insurer || undefined);

  const add = async () => {
    if (!percentage) return;
    await createOverride.mutateAsync({
      channel: channelId,
      insurer: insurer || null,
      insurance_type: insuranceType || null,
      commission_percentage: percentage,
      premium_base: premiumBase,
    });
    setPercentage('');
    setInsuranceType('');
  };

  return (
    <div className="space-y-4">
      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : overrides.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No overrides — this channel uses its channel type's payout grid.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Insurer</th>
                <th className="px-3 py-2 text-left font-medium">Insurance Type</th>
                <th className="px-3 py-2 text-left font-medium">Percentage</th>
                <th className="px-3 py-2 text-left font-medium">Commission On</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {overrides.map((o) => (
                <tr key={o.id}>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{o.insurer_name || 'All insurers'}</td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{o.insurance_type_name || 'All types'}</td>
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">{o.commission_percentage}%</td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                    {channelPayoutBasisOptions.find((p) => p.value === o.premium_base)?.label || o.premium_base}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      onClick={() => deleteOverride.mutate(o.id)}
                      title="Remove override"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Select
          label="Insurer (optional)"
          value={insurer}
          onChange={(e) => {
            setInsurer(e.target.value);
            setInsuranceType('');
          }}
          options={(insurers || []).map((i) => ({ value: i.id, label: i.name }))}
          placeholder="All insurers"
        />
        <Select
          label="Insurance Type (optional)"
          value={insuranceType}
          onChange={(e) => setInsuranceType(e.target.value)}
          options={(insurerTypes || []).map((t) => ({ value: t.id, label: t.name }))}
          placeholder="All types"
        />
        <Input
          label="Percentage"
          type="number"
          step="0.01"
          value={percentage}
          onChange={(e) => setPercentage(e.target.value)}
        />
        <div>
          <Select
            label="Commission On"
            value={premiumBase}
            onChange={(e) => setPremiumBase(e.target.value)}
            options={channelPayoutBasisOptions}
          />
          {basisHidesOdTp(premiumBase) && (
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">{BROKER_COMMISSION_HINT}</p>
          )}
        </div>
      </div>
      <Button onClick={add} isLoading={createOverride.isPending} disabled={!percentage}>
        Add Override
      </Button>
    </div>
  );
}
